// @mui material components
import Card from "@mui/material/Card";
import { Grid, Tooltip, IconButton, Stack, Chip, TextField, Collapse, Icon } from "@mui/material";

// Custom components
import RejectRequestModal from "components/SubscriptionExtendRequests/RejectRequestModal";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DraggableDialog from "components/draggagle-dialog";
import MDButton from "components/MDButton";

// Material Dashboard 2 PRO React examples
import DataTable from "examples/Tables/DataTable";
import CustomPageLayout from "examples/LayoutContainers/CustomPageLayout";

// React hooks and utilities
import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment";

// Icons
import { Visibility, CheckCircle, Cancel, Refresh, FilterList } from "@mui/icons-material";

// Context and API
import { LoginFormContext } from "context/loginFormCtx";
import {
  getSubscriptionExtendRequests,
  updateSubscriptionExtendRequest,
  getPendingSubscriptionExtendRequestsCount,
} from "lib/Api/SubscriptionExtendRequests";

// Components and utilities
import DefaultCell from "examples/Tables/components/DefaultCell";
import { SUBSCRIPTION_EXTEND_REQUEST_STATUS, TABLE_FILTERS } from "utils/constants";
import {
  getFiltersFromCache,
  saveFiltersToCache,
  getSortByFromCache,
  saveSortByToCache,
} from "utils/cacheManager";

/**
 * Tenant Subscription Extend Requests Section Component
 *
 * This component provides a comprehensive admin interface for managing subscription extend requests
 * for a specific tenant. It includes features like filtering, pagination, approval/rejection actions,
 * and real-time updates, similar to the main subscription extend requests page.
 */
function TenantRequestsSection({ tenantId }) {
  // Context for notifications
  const { setNotification, showMessageNotification } = useContext(LoginFormContext);

  // State management for data and UI
  const [totalPages, setTotalPages] = useState(0);
  const [rowCount, setRowCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  // Modal states
  const [openDetailsModal, setOpenDetailsModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [openRejectModal, setOpenRejectModal] = useState(false);

  // Dropdown states for modal sections
  const [showRequestReason, setShowRequestReason] = useState(false);
  const [showRejectReason, setShowRejectReason] = useState(false);

  // Pagination and filtering states
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [filters, setFilters] = useState(getFiltersFromCache(TABLE_FILTERS.SUBSCRIPTION_EXTEND_REQUESTS) || []);
  const [sortBy, setSortBy] = useState(
    getSortByFromCache(TABLE_FILTERS.SUBSCRIPTION_EXTEND_REQUESTS) || [{ id: "created_at", desc: true }]
  );

  // Data table columns configuration - Same structure as main page
  const [dataTableColumns, setDataTableColumns] = useState([
    {
      Header: "Company Name",
      accessor: "tenant_id",
      sort: true,
      Cell: ({ value, row }) => {
        const tenant = row.original.tenant;
        return <DefaultCell value={tenant ? `${tenant.company_name}` : "N/A"} />;
      },
    },
    {
      Header: "Status",
      accessor: "status",
      filterInputType: "select",
      filterOptions: [
        { value: 1, label: "Pending" },
        { value: 2, label: "Approved" },
        { value: 3, label: "Rejected" },
      ],
      sort: true,
      Cell: ({ value }) => {
        const statusConfig = SUBSCRIPTION_EXTEND_REQUEST_STATUS.find((s) => s.id === value);
        if (!statusConfig) return <DefaultCell value="Unknown" />;

        return (
          <MDButton
            color={statusConfig.color}
            sx={{
              background: statusConfig.background,
              cursor: "default",
              minWidth: "80px",
            }}
            size="small"
            circular={true}
          >
            {statusConfig.label}
          </MDButton>
        );
      },
    },
    {
      Header: "Updated At",
      accessor: "updated_at",
      filterInputType: "date",
      sort: true,
      Cell: ({ value }) => (
        <DefaultCell value={value ? moment(value).format("MM/DD/YYYY") : "N/A"} />
      ),
    },
    {
      Header: "Created",
      accessor: "created_at",
      filterInputType: "date",
      sort: true,
      Cell: ({ value }) => (
        <DefaultCell value={value ? moment(value).format("MM/DD/YYYY") : "N/A"} />
      ),
    },
    {
      Header: "Actions",
      sort: false,
      Cell: ({ value, row }) => {
        const request = row.original;
        const isPending = request.status === 1;

        return (
          <MDBox>
            {/* View Details Button */}
            <Tooltip title="View Details" placement="top">
              <IconButton onClick={() => handleViewDetails(request)}>
                <Visibility />
              </IconButton>
            </Tooltip>

            {/* Approve Button - Only for pending requests */}
            {isPending && (
              <Tooltip title="Approve Request" placement="top">
                <IconButton onClick={() => handleActionClick(request, "approve")}>
                  <CheckCircle />
                </IconButton>
              </Tooltip>
            )}

            {/* Reject Button - Only for pending requests */}
            {isPending && (
              <Tooltip title="Reject Request" placement="top">
                <IconButton onClick={() => handleActionClick(request, "reject")}>
                  <Cancel />
                </IconButton>
              </Tooltip>
            )}
          </MDBox>
        );
      },
    },
  ]);

  // Table data state
  const [tableData, setTableData] = useState({
    columns: dataTableColumns,
    rows: [],
  });

  /**
   * Fetch subscription extend requests data from API for this tenant
   */
  const getSubscriptionExtendRequestsData = async () => {
    try {
      setIsLoading(true);

      const sortId = sortBy[0]?.id;
      const sortVal = sortBy[0]?.desc ? "desc" : "asc";

      const updatedFilters = filters.map((filter) => {
        if (filter.id == "status") {
          return {
            ...filter,
            value: filter.value.value,
          };
        }
        return filter;
      });

      const filterBody = {
        page: page + 1,
        pageSize,
        filters: [...updatedFilters, { id: "tenant_id", value: tenantId }],
        sortBy: sortId ? [{ id: sortId, value: sortVal }] : [],
      };

      const response = await getSubscriptionExtendRequests(filterBody);

      if (response && response.status === 200) {
        setTableData({
          ...tableData,
          rows: response.data.data.rows.map((row) => ({
            ...row,
            plan_name: row.subscription?.plan?.name || "N/A",
            created_at: row.created_at || null,
            updated_at: row.updated_at || null,
          })),
        });
        setTotalPages(Math.ceil(response.data.data.count / pageSize));
        setRowCount(response?.data?.data?.count || 0);
      }
    } catch (error) {
      console.log("error (getSubscriptionExtendRequestsData): ", error);
      setNotification({
        color: "error",
        icon: "cancel",
        title: "Error",
        content: "Failed to fetch subscription extend requests. Please try again.",
        open: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Handle view details action
   */
  const handleViewDetails = (request) => {
    setSelectedRequest(request);
    setOpenDetailsModal(true);
  };

  /**
   * Handle approve/reject action click
   */
  const handleActionClick = (request, type) => {
    setSelectedRequest(request);

    // For reject action, show rejection modal
    if (type === "reject") {
      setOpenRejectModal(true);
      return;
    }

    // For approve action, show confirmation directly
    const confirmMessage = {
      title: "Are you sure?",
      text: `You want to approve this subscription extend request?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "var(--cc-confirm-button-color)",
      cancelButtonColor: "var(--cc-cancel-button-color)",
      confirmButtonText: "Yes, approve it!",
    };

    showMessageNotification(confirmMessage).then(async (result) => {
      if (result.isConfirmed) {
        const response = await updateSubscriptionExtendRequest(request.id, {
          status: type === "approve" ? 2 : 3,
          ...(type === "reject" && { reject_reason: result.value.trim() }),
        });

        if (response?.status === 200) {
          getSubscriptionExtendRequestsData();
          setNotification({
            color: "success",
            icon: "check",
            title: "Success",
            content: `Request ${type}d successfully!`,
            open: true,
          });
        } else {
          setNotification({
            color: "error",
            icon: "cancel",
            title: "Error",
            content: response.data.message,
            open: true,
          });
        }
      }
    });
  };

  // Pagination handlers
  const handlePageChange = (page) => {
    setPage(page);
  };

  const handlePageSizeChange = (pageSize) => {
    setPageSize(pageSize);
    setPage(0);
  };

  // Filter handlers
  const handleFiltersChange = (filterObject) => {
    setFilters(filterObject);
    saveFiltersToCache(TABLE_FILTERS.SUBSCRIPTION_EXTEND_REQUESTS, filterObject);
    setPage(0);
  };

  const handleClearAllFilters = () => {
    setFilters([]);
    saveFiltersToCache(TABLE_FILTERS.SUBSCRIPTION_EXTEND_REQUESTS, []);
    setPage(0);
  };

  // Sort handlers
  const handleSortByChange = (sortObject) => {
    setSortBy(sortObject);
    saveSortByToCache(TABLE_FILTERS.SUBSCRIPTION_EXTEND_REQUESTS, sortObject);
  };

  // Render details modal content
  const renderDetailsModal = () => {
    if (!selectedRequest) return null;

    const statusConfig = SUBSCRIPTION_EXTEND_REQUEST_STATUS.find(
      (s) => s.id === selectedRequest.status
    );

    return (
      <Stack spacing={2}>
        {/* Request ID and Status */}
        <MDBox display="flex" justifyContent="space-between" alignItems="center">
          <MDTypography variant="h5" fontWeight="medium">
            Request #{selectedRequest.id}
          </MDTypography>
          <MDButton
            color={statusConfig?.color || "default"}
            sx={{
              background: statusConfig?.background,
              cursor: "default",
            }}
            size="small"
            circular={true}
          >
            {statusConfig?.label || "Unknown"}
          </MDButton>
        </MDBox>

        {/* Tenant and Subscription Details - Side by Side */}
        <MDBox display="flex" gap={3}>
          {/* Tenant Information */}
          <MDBox flex={1}>
            <MDTypography variant="h6" fontWeight="medium" mb={1}>
              Tenant Information
            </MDTypography>
            <MDBox flex={1} sx={{ display: "flex", flexDirection: "column" }}>
              <MDTypography variant="button">
                Company: {selectedRequest.tenant?.company_name || "N/A"}
              </MDTypography>
              <MDTypography variant="button">
                Name: {selectedRequest.subscription?.user?.first_name}{" "}
                {selectedRequest.subscription?.user?.last_name}
              </MDTypography>
              {selectedRequest.subscription?.user?.email && (
                <MDTypography variant="button">
                  Email: {selectedRequest.subscription?.user?.email}
                </MDTypography>
              )}
              {selectedRequest.tenant?.subdomain && (
                <MDTypography variant="button">
                  Subdomain: {selectedRequest.tenant.subdomain}
                </MDTypography>
              )}
            </MDBox>
          </MDBox>

          {/* Subscription Details */}
          <MDBox flex={1}>
            <MDTypography variant="h6" fontWeight="medium" mb={1}>
              Subscription Details
            </MDTypography>
            <MDTypography variant="button" display="block">
              Plan: {selectedRequest.subscription?.plan?.name || "N/A"}
            </MDTypography>
            <MDTypography variant="button" display="block">
              End Date:{" "}
              {selectedRequest.subscription?.end_date
                ? moment(selectedRequest.subscription.end_date).format("MM/DD/YYYY")
                : "N/A"}
            </MDTypography>
          </MDBox>
        </MDBox>

        {/* Request Reason - Collapsible */}
        {selectedRequest.request_reason && (
          <MDBox>
            <MDBox
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              sx={{
                cursor: "pointer",
                p: 2,
                borderRadius: "8px",
                backgroundColor: "#f8f9fa",
                transition: "all 0.2s ease-in-out",
              }}
              onClick={() => setShowRequestReason(!showRequestReason)}
            >
              <MDTypography variant="h6" fontWeight="medium" color="text">
                Request Reason
              </MDTypography>
              <MDBox
                sx={{
                  transform: showRequestReason ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "transform 0.2s ease-in-out",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <Icon sx={{ fontSize: "1.2rem", color: "text.secondary" }}>expand_more</Icon>
              </MDBox>
            </MDBox>
            <Collapse in={showRequestReason} unmountOnExit>
              <MDBox
                p={2}
                mt={1}
                sx={{
                  backgroundColor: "#ffffff",
                  borderRadius: "8px",
                  border: "1px solid #e9ecef",
                  boxShadow: "inset 0 1px 3px rgba(0,0,0,0.05)",
                }}
              >
                <MDTypography variant="button">
                  <MDBox sx={{
                    maxHeight: "200px",
                    overflowY: "auto",
                  }}>
                  {selectedRequest.request_reason}
                  </MDBox>
                  </MDTypography>
              </MDBox>
            </Collapse>
          </MDBox>
        )}

        {/* Rejection Reason - Collapsible */}
        {selectedRequest.reject_reason && (
          <MDBox>
            <MDBox
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              sx={{
                cursor: "pointer",
                p: 2,
                borderRadius: "8px",
                backgroundColor: "#fff5f5",
                transition: "all 0.2s ease-in-out",
              }}
              onClick={() => setShowRejectReason(!showRejectReason)}
            >
              <MDTypography variant="h6" fontWeight="medium" color="error">
                Rejection Reason
              </MDTypography>
              <MDBox
                sx={{
                  transform: showRejectReason ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "transform 0.2s ease-in-out",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <Icon sx={{ fontSize: "1.2rem", color: "error.main" }}>expand_more</Icon>
              </MDBox>
            </MDBox>
            <Collapse in={showRejectReason} unmountOnExit>
              <MDBox
                p={2}
                mt={1}
                sx={{
                  backgroundColor: "#ffffff",
                  borderRadius: "8px",
                  border: "1px solid #ffcccc",
                  boxShadow: "inset 0 1px 3px rgba(255,0,0,0.05)",
                }}
              >
                <MDTypography variant="button" color="error">
                <MDBox sx={{
                    maxHeight: "200px",
                    overflowY: "auto",
                  }}>
                  {selectedRequest.reject_reason}
                  </MDBox>
                </MDTypography>
              </MDBox>
            </Collapse>
          </MDBox>
        )}

        {/* Action Buttons - Only show for pending requests */}
        {selectedRequest.status === 1 && (
          <MDBox display="flex" gap={2} justifyContent="flex-end" mt={2}>
            <MDButton
              color="primary"
              variant="contained"
              onClick={() => handleActionClick(selectedRequest, "approve")}
            >
              Accept Request
            </MDButton>
            <MDButton
              color="error"
              variant="outlined"
              onClick={() => handleActionClick(selectedRequest, "reject")}
            >
              Reject Request
            </MDButton>
          </MDBox>
        )}
      </Stack>
    );
  };

  useEffect(() => {
    setTableData({
      ...tableData,
      columns: dataTableColumns,
    });
  }, [dataTableColumns]);

  useEffect(() => {
    if (tenantId) {
      getSubscriptionExtendRequestsData();
    }
  }, [page, pageSize, filters, sortBy, tenantId]);

  // If no tenantId, don't render
  if (!tenantId) {
    return null;
  }

  return (
    <MDBox py={3}>
      <Card>
        {/* Header Section */}
        <MDBox p={3} lineHeight={1}>
          <Grid container justifyContent="space-between" alignItems="center" spacing={3}>
            <Grid item>
              <MDTypography variant="h5" fontWeight="medium">
                Trial Extension Requests
              </MDTypography>
              <MDTypography variant="button" color="text">
                Manage trial extension requests for this tenant
              </MDTypography>
            </Grid>
          </Grid>
        </MDBox>

        <DataTable
          isLoading={isLoading}
          table={tableData}
          pageSize={pageSize}
          currentPage={page}
          totalPages={totalPages}
          handlePageChange={handlePageChange}
          handlePageSizeChange={handlePageSizeChange}
          filters={filters}
          handleFiltersChange={handleFiltersChange}
          sorting={sortBy}
          handleSortByChange={handleSortByChange}
          getData={getSubscriptionExtendRequestsData}
          handleClearAllFilters={handleClearAllFilters}
          loading={isLoading}
          rowCount={rowCount}
        />
      </Card>

      {/* Details Modal */}
      <DraggableDialog
        open={openDetailsModal}
        onClose={() => setOpenDetailsModal(false)}
        dialogTitle="Request Details"
        dialogContent={renderDetailsModal()}
        maxWidth="xl"
      />

      {/* Reject Modal */}
      <RejectRequestModal
        openState={openRejectModal}
        handleFormClose={() => setOpenRejectModal(false)}
        selectedRequest={selectedRequest}
        onReject={async (description) => {
          const response = await updateSubscriptionExtendRequest(selectedRequest.id, {
            status: 3,
            reject_reason: description,
          });

          if (response?.status === 200) {
            setNotification({
              color: "success",
              icon: "check",
              title: "Success",
              content: "Request rejected successfully!",
              open: true,
            });

            // Close modal and refresh data
            setOpenRejectModal(false);
            getSubscriptionExtendRequestsData();
          } else {
            setNotification({
              color: "error",
              icon: "cancel",
              title: "Error",
              content: response.data.message,
              open: true,
            });
          }
        }}
      />
    </MDBox>
  );
}

export default TenantRequestsSection;
